import { b as ImxConfiguration, c as convertToSignableToken, s as signRaw, e as exportContracts, d as signMessage, E as EncodingApi, M as MintsApi, f as signRegisterEthAddress, A as ApiConfiguration, h as AssetsApi, B as BalancesApi, C as CollectionsApi, D as DepositsApi, i as ExchangesApi, I as IMXClient, j as IMXError, k as ImmutableX, l as MetadataApi, m as MetadataRefreshesApi, n as MetadataSchemaRequestTypeEnum, N as NftCheckoutPrimaryApi, O as OrdersApi, P as ProjectsApi, T as TokensApi, o as TradesApi, p as TransfersApi, U as UsersApi, W as WithdrawalsApi, q as createConfig, r as createImmutableXConfiguration, a as createStarkSigner, g as generateLegacyStarkPrivateKey, t as generateStarkPrivateKey, u as imxClientConfig, v as production, w as sandbox, x as serializePackedSignature, y as starkEcOrder } from './index-CTcEmIj1.js';
import { d as index$2 } from './index-C80azzXH.js';
import { isAxiosError } from 'axios';
import * as encUtils from 'enc-utils';
import { parseUnits } from '@ethersproject/units';
import { BigNumber } from '@ethersproject/bignumber';
import { ethers } from 'ethers';
import detectEthereumProvider from '@metamask/detect-provider';
import { E as Environment, I as ImmutableConfiguration } from './index-Bp8cN2wo.js';
import { Signer } from '@ethersproject/abstract-signer';

function isChainValid(chainID, config) {
    return chainID === config.ethConfiguration.chainID;
}
async function validateChain(signer, config) {
    const chainID = await signer.getChainId();
    if (!isChainValid(chainID, config)) {
        throw new Error('The wallet used for this operation is not connected to the correct network.');
    }
}

async function transfer({ signers: { ethSigner, starkSigner }, request, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    const ethAddress = await ethSigner.getAddress();
    const transfersApi = new index$2.TransfersApi(config.immutableXConfig.apiConfiguration);
    const transferAmount = request.type === 'ERC721' ? '1' : request.amount;
    const signableResult = await transfersApi.getSignableTransferV1({
        getSignableTransferRequest: {
            sender: ethAddress,
            token: convertToSignableToken(request),
            amount: transferAmount,
            receiver: request.receiver,
        },
    });
    const { signable_message: signableMessage, payload_hash: payloadHash } = signableResult.data;
    const ethSignature = await signRaw(signableMessage, ethSigner);
    const starkSignature = await starkSigner.signMessage(payloadHash);
    const transferSigningParams = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sender_stark_key: signableResult.data.sender_stark_key,
        sender_vault_id: signableResult.data.sender_vault_id,
        receiver_stark_key: signableResult.data.receiver_stark_key,
        receiver_vault_id: signableResult.data.receiver_vault_id,
        asset_id: signableResult.data.asset_id,
        amount: signableResult.data.amount,
        nonce: signableResult.data.nonce,
        expiration_timestamp: signableResult.data.expiration_timestamp,
        stark_signature: starkSignature,
    };
    const response = await transfersApi.createTransferV1({
        createTransferRequest: transferSigningParams,
        xImxEthAddress: ethAddress,
        xImxEthSignature: ethSignature,
    });
    return {
        sent_signature: response?.data.sent_signature,
        status: response?.data.status?.toString(),
        time: response?.data.time,
        transfer_id: response?.data.transfer_id,
    };
}
async function batchTransfer({ signers: { ethSigner, starkSigner }, request, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    const ethAddress = await ethSigner.getAddress();
    const transfersApi = new index$2.TransfersApi(config.immutableXConfig.apiConfiguration);
    const signableRequests = request.map((nftTransfer) => ({
        amount: '1',
        token: convertToSignableToken({
            type: 'ERC721',
            tokenId: nftTransfer.tokenId,
            tokenAddress: nftTransfer.tokenAddress,
        }),
        receiver: nftTransfer.receiver,
    }));
    const signableResult = await transfersApi.getSignableTransfer({
        getSignableTransferRequestV2: {
            sender_ether_key: ethAddress,
            signable_requests: signableRequests,
        },
    });
    const signableMessage = signableResult.data.signable_message;
    if (signableMessage === undefined) {
        throw new Error('Invalid response from Signable registration offchain');
    }
    const ethSignature = await signRaw(signableMessage, ethSigner);
    const requests = [];
    for (const resp of signableResult.data.signable_responses) {
        // TODO: remove once fixed
        // eslint-disable-next-line no-await-in-loop
        const starkSignature = await starkSigner.signMessage(resp.payload_hash);
        const req = {
            sender_vault_id: resp.sender_vault_id,
            receiver_stark_key: resp.receiver_stark_key,
            receiver_vault_id: resp.receiver_vault_id,
            asset_id: resp.asset_id,
            amount: resp.amount,
            nonce: resp.nonce,
            expiration_timestamp: resp.expiration_timestamp,
            stark_signature: starkSignature,
        };
        requests.push(req);
    }
    // TODO: throw error on missing payload hash?
    const transferSigningParams = {
        sender_stark_key: signableResult.data.sender_stark_key,
        requests,
    };
    const response = await transfersApi.createTransfer({
        createTransferRequestV2: transferSigningParams,
        xImxEthAddress: ethAddress,
        xImxEthSignature: ethSignature,
    });
    return {
        transfer_ids: response?.data.transfer_ids,
    };
}

async function createOrder({ signers, request, config, }) {
    await validateChain(signers.ethSigner, config.immutableXConfig);
    const ethAddress = await signers.ethSigner.getAddress();
    const ordersApi = new index$2.OrdersApi(config.immutableXConfig.apiConfiguration);
    const amountSell = request.sell.type === 'ERC721' ? '1' : request.sell.amount;
    const amountBuy = request.buy.type === 'ERC721' ? '1' : request.buy.amount;
    const getSignableOrderRequest = {
        user: ethAddress,
        amount_buy: amountBuy,
        token_buy: convertToSignableToken(request.buy),
        amount_sell: amountSell,
        token_sell: convertToSignableToken(request.sell),
        fees: request.fees,
        expiration_timestamp: request.expiration_timestamp,
    };
    const getSignableOrderResponse = await ordersApi.getSignableOrder({
        getSignableOrderRequestV3: getSignableOrderRequest,
    });
    const { signable_message: signableMessage, payload_hash: payloadHash } = getSignableOrderResponse.data;
    const ethSignature = await signRaw(signableMessage, signers.ethSigner);
    const starkSignature = await signers.starkSigner.signMessage(payloadHash);
    const resp = getSignableOrderResponse.data;
    const orderParams = {
        createOrderRequest: {
            amount_buy: resp.amount_buy,
            amount_sell: resp.amount_sell,
            asset_id_buy: resp.asset_id_buy,
            asset_id_sell: resp.asset_id_sell,
            expiration_timestamp: resp.expiration_timestamp,
            fees: request.fees,
            nonce: resp.nonce,
            stark_key: resp.stark_key,
            stark_signature: starkSignature,
            vault_id_buy: resp.vault_id_buy,
            vault_id_sell: resp.vault_id_sell,
        },
        xImxEthAddress: ethAddress,
        xImxEthSignature: ethSignature,
    };
    const createOrderResponse = await ordersApi.createOrderV3(orderParams);
    return {
        ...createOrderResponse.data,
    };
}
async function cancelOrder({ signers, request, config, }) {
    const ordersApi = new index$2.OrdersApi(config.immutableXConfig.apiConfiguration);
    const getSignableCancelOrderResponse = await ordersApi.getSignableCancelOrderV3({
        getSignableCancelOrderRequest: {
            order_id: request.order_id,
        },
    });
    const { signable_message: signableMessage, payload_hash: payloadHash } = getSignableCancelOrderResponse.data;
    const ethSignature = await signRaw(signableMessage, signers.ethSigner);
    const starkSignature = await signers.starkSigner.signMessage(payloadHash);
    const ethAddress = await signers.ethSigner.getAddress();
    const cancelOrderResponse = await ordersApi.cancelOrderV3({
        id: request.order_id.toString(),
        cancelOrderRequest: {
            order_id: request.order_id,
            stark_signature: starkSignature,
        },
        xImxEthAddress: ethAddress,
        xImxEthSignature: ethSignature,
    });
    return {
        order_id: cancelOrderResponse.data.order_id,
        status: cancelOrderResponse.data.status,
    };
}

async function registerOffchain(signers, config) {
    await validateChain(signers.ethSigner, config.immutableXConfig);
    const usersApi = new index$2.UsersApi(config.immutableXConfig.apiConfiguration);
    const userAddress = await signers.ethSigner.getAddress();
    const starkPublicKey = await signers.starkSigner.getAddress();
    const signableResult = await usersApi.getSignableRegistrationOffchain({
        getSignableRegistrationRequest: {
            ether_key: userAddress,
            stark_key: starkPublicKey,
        },
    });
    const { signable_message: signableMessage, payload_hash: payloadHash } = signableResult.data;
    const ethSignature = await signRaw(signableMessage, signers.ethSigner);
    const starkSignature = await signers.starkSigner.signMessage(payloadHash);
    const registeredUser = await usersApi.registerUser({
        registerUserRequest: {
            eth_signature: ethSignature,
            ether_key: userAddress,
            stark_signature: starkSignature,
            stark_key: starkPublicKey,
        },
    });
    return registeredUser.data;
}
async function isRegisteredOffchain(ethAddress, config) {
    try {
        const usersApi = new index$2.UsersApi(config.immutableXConfig.apiConfiguration);
        const getUsersResult = await usersApi.getUsers({
            user: ethAddress,
        });
        const { accounts } = getUsersResult.data;
        return accounts?.length > 0;
    }
    catch (ex) {
        if (isAxiosError(ex) && ex.response?.status === 404) {
            return false;
        }
        throw ex;
    }
}
async function isRegisteredOnChain(starkPublicKey, ethSigner, config) {
    await validateChain(ethSigner, config.immutableXConfig);
    const imxConfig = config.immutableXConfig;
    const registrationContract = exportContracts.RegistrationV4.connect(imxConfig.ethConfiguration.registrationV4ContractAddress || imxConfig.ethConfiguration.registrationContractAddress, ethSigner);
    try {
        return await registrationContract.isRegistered(starkPublicKey);
    }
    catch (ex) {
        if (ex.reason === 'USER_UNREGISTERED') {
            return false;
        }
        throw ex;
    }
}

const assertIsDefined = (value) => {
    if (value !== undefined)
        return value;
    throw new Error('undefined field exception');
};
async function prepareWithdrawalAction(params, withdrawalsApi) {
    const { signers: { ethSigner, starkSigner }, } = params;
    await validateChain(ethSigner, params.config);
    const withdrawalAmount = params.type === 'ERC721' ? '1' : params.amount;
    const signableWithdrawalResult = await withdrawalsApi.getSignableWithdrawalV2({
        getSignableWithdrawalRequest: {
            user: await ethSigner.getAddress(),
            token: convertToSignableToken(params),
            amount: withdrawalAmount,
        },
    });
    const { signable_message: signableMessage, payload_hash: payloadHash } = signableWithdrawalResult.data;
    const starkSignature = await starkSigner.signMessage(payloadHash);
    const { ethAddress, ethSignature } = await signMessage(signableMessage, ethSigner);
    const prepareWithdrawalResponse = await withdrawalsApi.createWithdrawalV2({
        createWithdrawalRequestV2: {
            sender_stark_key: assertIsDefined(signableWithdrawalResult.data.sender_stark_key),
            sender_vault_id: assertIsDefined(signableWithdrawalResult.data.sender_vault_id),
            receiver_stark_key: assertIsDefined(signableWithdrawalResult.data.receiver_stark_key),
            receiver_vault_id: assertIsDefined(signableWithdrawalResult.data.receiver_vault_id),
            amount: withdrawalAmount,
            asset_id: assertIsDefined(signableWithdrawalResult.data.asset_id),
            expiration_timestamp: assertIsDefined(signableWithdrawalResult.data.expiration_timestamp),
            nonce: assertIsDefined(signableWithdrawalResult.data.nonce),
            stark_signature: starkSignature,
        },
        xImxEthAddress: ethAddress,
        xImxEthSignature: ethSignature,
    });
    return prepareWithdrawalResponse.data;
}

async function getEncodeAssetInfo(assetType, tokenType, config, tokenData) {
    const encodingApi = new index$2.EncodingApi(config.apiConfiguration);
    const result = await encodingApi.encodeAsset({
        assetType,
        encodeAssetRequest: {
            token: {
                type: tokenType,
                ...(tokenData && { data: tokenData }),
            },
        },
    });
    return result.data;
}

async function getWithdrawalBalance(signer, ownerKey, assetId, config) {
    const coreContract = exportContracts.CoreV4.connect(config.ethConfiguration.coreContractAddress, signer);
    return coreContract.getWithdrawalBalance(ownerKey, assetId);
}
async function getETHWithdrawalBalance(signer, ownerKey, config) {
    const assetType = await getEncodeAssetInfo('asset', 'ETH', config);
    return await getWithdrawalBalance(signer, ownerKey, assetType.asset_id, config);
}
async function getERC20WithdrawalBalance(signer, ownerKey, tokenAddress, config) {
    const assetType = await getEncodeAssetInfo('asset', 'ERC20', config, {
        token_address: tokenAddress,
    });
    return await getWithdrawalBalance(signer, ownerKey, assetType.asset_id, config);
}
async function getERC721WithdrawalBalance(signer, ownerKey, token, encodingApi, mintsApi, config) {
    try {
        const mintableToken = await mintsApi
            .getMintableTokenDetailsByClientTokenId({
            tokenAddress: token.tokenAddress,
            tokenId: token.tokenId,
        });
        const assetType = await getEncodeAssetInfo('mintable-asset', 'ERC721', config, {
            id: token.tokenId,
            token_address: token.tokenAddress,
            ...(mintableToken.data.blueprint && {
                blueprint: mintableToken.data.blueprint,
            }),
        });
        return await getWithdrawalBalance(signer, ownerKey, assetType.asset_id, config);
    }
    catch (error) {
        if (error.response?.status === 404) {
            // token is not a mintable ERC721 token
            const assetType = await getEncodeAssetInfo('asset', 'ERC721', config, {
                token_id: token.tokenId,
                token_address: token.tokenAddress,
            });
            return await getWithdrawalBalance(signer, ownerKey, assetType.asset_id, config);
        }
        throw error; // unable to recover from any other kind of error
    }
}
async function getWithdrawalBalanceWorkflow(signer, ownerKey, token, encodingApi, mintsApi, config) {
    switch (token.type) {
        case 'ETH':
            return await getETHWithdrawalBalance(signer, ownerKey, config);
        case 'ERC20':
            return await getERC20WithdrawalBalance(signer, ownerKey, token.tokenAddress, config);
        case 'ERC721':
            return await getERC721WithdrawalBalance(signer, ownerKey, token, encodingApi, mintsApi, config);
        default:
            throw new Error('Unsupported token type');
    }
}
async function getWithdrawalBalances(signer, starkPublicKey, ethAddress, token, config) {
    const encodingApi = new EncodingApi(config.apiConfiguration);
    const mintsApi = new MintsApi(config.apiConfiguration);
    const v3Balance = await getWithdrawalBalanceWorkflow(signer, starkPublicKey, token, encodingApi, mintsApi, config);
    const v4Balance = await getWithdrawalBalanceWorkflow(signer, ethAddress, token, encodingApi, mintsApi, config);
    return {
        v3Balance,
        v4Balance,
    };
}
async function getWithdrawalBalancesERC721(signer, starkPublicKey, ethAddress, token, config, mintsApi) {
    const encodingApi = new EncodingApi(config.apiConfiguration);
    const v3Balance = await getWithdrawalBalanceWorkflow(signer, starkPublicKey, token, encodingApi, mintsApi, config);
    const v4Balance = await getWithdrawalBalanceWorkflow(signer, ethAddress, token, encodingApi, mintsApi, config);
    return {
        v3Balance,
        v4Balance,
    };
}

const ERC20TokenType = 'ERC20';
async function executeRegisterAndWithdrawAllFungible(ethSigner, starkSigner, starkPublicKey, assetType, config) {
    const etherKey = await ethSigner.getAddress();
    const starkSignature = await signRegisterEthAddress(starkSigner, etherKey, starkPublicKey);
    // we use registration v4 contract as a wrapper for the core contract
    // so that v3 and v4 withdrawals, AND on-chain registration can be executed in a single transaction
    const contract = exportContracts.RegistrationV4.connect(config.ethConfiguration.registrationV4ContractAddress || config.ethConfiguration.registrationContractAddress, ethSigner);
    const populatedTransaction = await contract.populateTransaction.registerAndWithdrawAll(etherKey, starkPublicKey, starkSignature, assetType);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function executeWithdrawAllFungible(ethSigner, starkPublicKey, assetType, config) {
    // we use registration v4 contract as a wrapper for the core contract
    // so that v3 and v4 withdrawals can be executed in a single transaction
    // (if there are pending withdrawable funds for both)
    const contract = exportContracts.RegistrationV4.connect(config.ethConfiguration.registrationV4ContractAddress || config.ethConfiguration.registrationContractAddress, ethSigner);
    const populatedTransaction = await contract.populateTransaction.withdrawAll(await ethSigner.getAddress(), starkPublicKey, assetType);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function executeWithdrawFungible(ethSigner, starkPublicKey, assetType, config) {
    const contract = exportContracts.CoreV4.connect(config.ethConfiguration.coreContractAddress, ethSigner);
    const populatedTransaction = await contract.populateTransaction.withdraw(await ethSigner.getAddress(), assetType);
    return ethSigner.sendTransaction(populatedTransaction);
}
// equivilant to Core SDK completeERC20WithdrawalV1Workflow
// in src/workflows/withdrawal/completeERC20Withdrawal.ts
async function completeERC20WithdrawalAction({ ethSigner, starkSigner, starkPublicKey, token, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    const { v3Balance, v4Balance, } = await getWithdrawalBalances(ethSigner, starkPublicKey, await ethSigner.getAddress(), {
        type: ERC20TokenType,
        tokenAddress: token.tokenAddress,
    }, config.immutableXConfig);
    const assetType = await getEncodeAssetInfo('asset', ERC20TokenType, config.immutableXConfig, {
        token_address: token.tokenAddress,
    });
    if (!v3Balance.isZero() && !v3Balance.isNegative()) {
        const isRegistered = await isRegisteredOnChain(starkPublicKey, ethSigner, config);
        if (isRegistered) {
            return executeWithdrawAllFungible(ethSigner, starkPublicKey, assetType.asset_type, config.immutableXConfig);
        }
        return executeRegisterAndWithdrawAllFungible(ethSigner, starkSigner, starkPublicKey, assetType.asset_type, config.immutableXConfig);
    }
    if (!v4Balance.isZero() && !v4Balance.isNegative()) {
        return executeWithdrawFungible(ethSigner, starkPublicKey, assetType.asset_type, config.immutableXConfig);
    }
    throw new Error('No balance to withdraw');
}

const ERC721TokenType = 'ERC721';
function getMintingBlob(token) {
    const { id } = token.data;
    const blueprint = token.data.blueprint || '';
    return encUtils.sanitizeHex(encUtils.utf8ToHex(`{${id}}:{${blueprint}}`));
}
async function executeERC721RegisterAndWithdraw(ethSigner, starkSigner, token, config) {
    const etherKey = await ethSigner.getAddress();
    const starkPublicKey = await starkSigner.getAddress();
    const assetType = await getEncodeAssetInfo('asset', ERC721TokenType, config, {
        token_id: token.tokenId,
        token_address: token.tokenAddress,
    });
    const registrationStarkSignature = await signRegisterEthAddress(starkSigner, etherKey, starkPublicKey);
    const contract = exportContracts.RegistrationV4.connect(config.ethConfiguration.registrationV4ContractAddress || config.ethConfiguration.registrationContractAddress, ethSigner);
    const populatedTransaction = await contract.populateTransaction.registerAndWithdrawNft(etherKey, starkPublicKey, registrationStarkSignature, assetType.asset_type, token.tokenId);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function executeMintableERC721RegisterAndWithdraw(ethSigner, starkSigner, token, config) {
    const etherKey = await ethSigner.getAddress();
    const starkPublicKey = await starkSigner.getAddress();
    const assetType = await getEncodeAssetInfo('mintable-asset', ERC721TokenType, config, {
        id: token.data.id,
        token_address: token.data.tokenAddress,
        ...(token.data.blueprint && { blueprint: token.data.blueprint }),
    });
    const mintingBlob = getMintingBlob(token);
    const starkSignature = await signRegisterEthAddress(starkSigner, etherKey, starkPublicKey);
    const contract = exportContracts.RegistrationV4.connect(config.ethConfiguration.registrationV4ContractAddress || config.ethConfiguration.registrationContractAddress, ethSigner);
    const populatedTransaction = await contract.populateTransaction.registerWithdrawAndMint(etherKey, starkPublicKey, starkSignature, assetType.asset_type, mintingBlob);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function completeERC721RegisterAndWithdrawal(mintsApi, ethSigner, starkSigner, token, config) {
    return mintsApi
        .getMintableTokenDetailsByClientTokenId({
        tokenAddress: token.tokenAddress,
        tokenId: token.tokenId,
    })
        .then((mintableToken) => executeMintableERC721RegisterAndWithdraw(ethSigner, starkSigner, {
        type: ERC721TokenType,
        data: {
            id: token.tokenId,
            tokenAddress: token.tokenAddress,
            blueprint: mintableToken.data.blueprint,
        },
    }, config))
        .catch((error) => {
        if (error.response?.status === 404) {
            // token is already minted on L1
            return executeERC721RegisterAndWithdraw(ethSigner, starkSigner, token, config);
        }
        throw error; // unable to recover from any other kind of error
    });
}
async function executeMintableERC721Withdrawal(ethSigner, ownerKey, token, config) {
    const assetType = await getEncodeAssetInfo('mintable-asset', ERC721TokenType, config, {
        id: token.data.id,
        token_address: token.data.tokenAddress,
        ...(token.data.blueprint && { blueprint: token.data.blueprint }),
    });
    const mintingBlob = getMintingBlob(token);
    const contract = exportContracts.CoreV4.connect(config.ethConfiguration.coreContractAddress, ethSigner);
    const populatedTransaction = await contract.populateTransaction.withdrawAndMint(ownerKey, assetType.asset_type, mintingBlob);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function executeERC721Withdrawal(ethSigner, ownerKey, token, config) {
    const assetType = await getEncodeAssetInfo('asset', ERC721TokenType, config, {
        token_id: token.tokenId,
        token_address: token.tokenAddress,
    });
    const contract = exportContracts.CoreV4.connect(config.ethConfiguration.coreContractAddress, ethSigner);
    const populatedTransaction = await contract.populateTransaction.withdrawNft(ownerKey, assetType.asset_type, token.tokenId);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function completeERC721Withdrawal(mintsApi, ethSigner, ownerKey, token, config) {
    return mintsApi
        .getMintableTokenDetailsByClientTokenId({
        tokenAddress: token.tokenAddress,
        tokenId: token.tokenId,
    })
        .then((mintableToken) => executeMintableERC721Withdrawal(ethSigner, ownerKey, {
        type: ERC721TokenType,
        data: {
            id: token.tokenId,
            tokenAddress: token.tokenAddress,
            blueprint: mintableToken.data.blueprint,
        },
    }, config))
        .catch((error) => {
        if (error.response?.status === 404) {
            // token is already minted on L1
            return executeERC721Withdrawal(ethSigner, ownerKey, token, config);
        }
        throw error; // unable to recover from any other kind of error
    });
}
async function completeERC721WithdrawalAction({ ethSigner, starkSigner, starkPublicKey, token, config, }, mintsApi) {
    await validateChain(ethSigner, config.immutableXConfig);
    const ethAddress = await ethSigner.getAddress();
    const { v3Balance, v4Balance, } = await getWithdrawalBalancesERC721(ethSigner, starkPublicKey, ethAddress, {
        type: ERC721TokenType,
        tokenAddress: token.tokenAddress,
        tokenId: token.tokenId,
    }, config.immutableXConfig, mintsApi);
    if (!v3Balance.isZero() && !v3Balance.isNegative()) {
        const isRegistered = await isRegisteredOnChain(starkPublicKey, ethSigner, config);
        // if the user is already registered on-chain, we can withdraw using stark key as the owner key
        if (isRegistered) {
            return completeERC721Withdrawal(mintsApi, ethSigner, starkPublicKey, token, config.immutableXConfig);
        }
        // if not registered on-chain, we need to register the user on-chain using stark public key as the owner key
        return completeERC721RegisterAndWithdrawal(mintsApi, ethSigner, starkSigner, token, config.immutableXConfig);
    }
    // if v4 balance is NOT zero, the withdrawal was prepared using eth address (using v2/withdrawals API)
    if (!v4Balance.isZero() && !v4Balance.isNegative()) {
        return completeERC721Withdrawal(mintsApi, ethSigner, ethAddress, token, config.immutableXConfig);
    }
    throw new Error('No balance to withdraw');
}

const EthTokenType = 'ETH';
async function completeEthWithdrawalAction({ ethSigner, starkSigner, starkPublicKey, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    // get withdrawal balances
    const { v3Balance, v4Balance, } = await getWithdrawalBalances(ethSigner, starkPublicKey, await ethSigner.getAddress(), { type: EthTokenType }, config.immutableXConfig);
    const assetType = await getEncodeAssetInfo('asset', EthTokenType, config.immutableXConfig);
    if (!v3Balance.isZero() && !v3Balance.isNegative()) {
        const isRegistered = await isRegisteredOnChain(starkPublicKey, ethSigner, config);
        if (isRegistered) {
            return executeWithdrawAllFungible(ethSigner, starkPublicKey, assetType.asset_type, config.immutableXConfig);
        }
        return executeRegisterAndWithdrawAllFungible(ethSigner, starkSigner, starkPublicKey, assetType.asset_type, config.immutableXConfig);
    }
    if (!v4Balance.isZero() && !v4Balance.isNegative()) {
        return executeWithdrawFungible(ethSigner, starkPublicKey, assetType.asset_type, config.immutableXConfig);
    }
    throw new Error('No balance to withdraw');
}

async function prepareWithdrawal({ signers, withdrawal, config, }) {
    const withdrawalsApi = new index$2.WithdrawalsApi(config.immutableXConfig.apiConfiguration);
    return prepareWithdrawalAction({
        signers,
        config: config.immutableXConfig,
        ...withdrawal,
    }, withdrawalsApi);
}
// TODO: remove once fixed
// eslint-disable-next-line consistent-return
async function completeWithdrawal({ signers: { ethSigner, starkSigner }, starkPublicKey, token, config, }) {
    const mintsApi = new index$2.MintsApi(config.immutableXConfig.apiConfiguration);
    // eslint-disable-next-line default-case
    switch (token.type) {
        case 'ETH':
            return completeEthWithdrawalAction({
                ethSigner, starkSigner, starkPublicKey, config,
            });
        case 'ERC20':
            return completeERC20WithdrawalAction({
                ethSigner,
                starkSigner,
                starkPublicKey,
                token,
                config,
            });
        case 'ERC721':
            return completeERC721WithdrawalAction({
                ethSigner,
                starkSigner,
                starkPublicKey,
                token,
                config,
            }, mintsApi);
    }
}

async function createTrade({ signers: { ethSigner, starkSigner }, request, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    const ethAddress = await ethSigner.getAddress();
    const tradesApi = new index$2.TradesApi(config.immutableXConfig.apiConfiguration);
    const signableResult = await tradesApi.getSignableTrade({
        getSignableTradeRequest: {
            user: ethAddress,
            order_id: request.order_id,
            fees: request.fees,
        },
    });
    const { signable_message: signableMessage, payload_hash: payloadHash } = signableResult.data;
    const ethSignature = await signRaw(signableMessage, ethSigner);
    const starkSignature = await starkSigner.signMessage(payloadHash);
    const createTradeResponse = await tradesApi.createTradeV3({
        createTradeRequest: {
            amount_buy: signableResult.data.amount_buy,
            amount_sell: signableResult.data.amount_sell,
            asset_id_buy: signableResult.data.asset_id_buy,
            asset_id_sell: signableResult.data.asset_id_sell,
            expiration_timestamp: signableResult.data.expiration_timestamp,
            fee_info: signableResult.data.fee_info,
            fees: request.fees,
            include_fees: true,
            nonce: signableResult.data.nonce,
            order_id: request.order_id,
            stark_key: signableResult.data.stark_key,
            vault_id_buy: signableResult.data.vault_id_buy,
            vault_id_sell: signableResult.data.vault_id_sell,
            stark_signature: starkSignature,
        },
        xImxEthAddress: ethAddress,
        xImxEthSignature: ethSignature,
    });
    return createTradeResponse.data;
}

async function executeDepositEth(ethSigner, amount, assetType, starkPublicKey, vaultId, config) {
    const coreContract = exportContracts.CoreV4.connect(config.ethConfiguration.coreContractAddress, ethSigner);
    const populatedTransaction = await coreContract.populateTransaction['deposit(uint256,uint256,uint256)'](starkPublicKey, assetType, vaultId);
    return ethSigner.sendTransaction({ ...populatedTransaction, value: amount });
}
async function depositEth({ signers: { ethSigner }, deposit, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    const user = await ethSigner.getAddress();
    const data = {
        decimals: 18,
    };
    const amount = parseUnits(deposit.amount, 'wei');
    const imxConfig = config.immutableXConfig;
    const depositsApi = new index$2.DepositsApi(imxConfig.apiConfiguration);
    const encodingApi = new index$2.EncodingApi(imxConfig.apiConfiguration);
    const getSignableDepositRequest = {
        user,
        token: {
            type: deposit.type,
            data,
        },
        amount: amount.toString(),
    };
    const signableDepositResult = await depositsApi.getSignableDeposit({
        getSignableDepositRequest,
    });
    const encodingResult = await encodingApi.encodeAsset({
        assetType: 'asset',
        encodeAssetRequest: {
            token: {
                type: deposit.type,
            },
        },
    });
    const assetType = encodingResult.data.asset_type;
    const starkPublicKey = signableDepositResult.data.stark_key;
    const vaultId = signableDepositResult.data.vault_id;
    return executeDepositEth(ethSigner, amount, assetType, starkPublicKey, vaultId, imxConfig);
}

async function executeDepositERC20(ethSigner, quantizedAmount, assetType, starkPublicKey, vaultId, config) {
    const coreContract = exportContracts.CoreV4.connect(config.coreContractAddress, ethSigner);
    const populatedTransaction = await coreContract.populateTransaction.depositERC20(starkPublicKey, assetType, vaultId, quantizedAmount);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function depositERC20({ signers: { ethSigner }, deposit, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    const { apiConfiguration, ethConfiguration } = config.immutableXConfig;
    const user = await ethSigner.getAddress();
    const tokensApi = new index$2.TokensApi(apiConfiguration);
    const depositsApi = new index$2.DepositsApi(apiConfiguration);
    const encodingApi = new index$2.EncodingApi(apiConfiguration);
    // Get decimals for this specific ERC20
    const token = await tokensApi.getToken({ address: deposit.tokenAddress });
    // TODO: remove once fixed
    // eslint-disable-next-line radix
    const decimals = parseInt(token.data.decimals);
    const data = {
        decimals,
        token_address: deposit.tokenAddress,
    };
    const amount = parseUnits(deposit.amount, 0); // 0 to always use undecimalized value
    // Approve whether an amount of token from an account can be spent by a third-party account
    const tokenContract = exportContracts.IERC20.connect(deposit.tokenAddress, ethSigner);
    const approveTransaction = await tokenContract.populateTransaction.approve(ethConfiguration.coreContractAddress, amount);
    await ethSigner.sendTransaction(approveTransaction);
    const getSignableDepositRequest = {
        user,
        token: {
            type: deposit.type,
            data,
        },
        amount: amount.toString(),
    };
    const signableDepositResult = await depositsApi.getSignableDeposit({
        getSignableDepositRequest,
    });
    // Perform encoding on asset details to get an assetType (required for stark contract request)
    const encodingResult = await encodingApi.encodeAsset({
        assetType: 'asset',
        encodeAssetRequest: {
            token: {
                type: deposit.type,
                data: {
                    token_address: deposit.tokenAddress,
                },
            },
        },
    });
    const assetType = encodingResult.data.asset_type;
    const starkPublicKey = signableDepositResult.data.stark_key;
    const vaultId = signableDepositResult.data.vault_id;
    const quantizedAmount = BigNumber.from(signableDepositResult.data.amount);
    return executeDepositERC20(ethSigner, quantizedAmount, assetType, starkPublicKey, vaultId, ethConfiguration);
}

async function executeDepositERC721(ethSigner, tokenId, assetType, starkPublicKey, vaultId, config) {
    const coreContract = exportContracts.CoreV4.connect(config.ethConfiguration.coreContractAddress, ethSigner);
    const populatedTransaction = await coreContract.populateTransaction.depositNft(starkPublicKey, assetType, vaultId, tokenId);
    return ethSigner.sendTransaction(populatedTransaction);
}
async function depositERC721({ signers: { ethSigner }, deposit, config, }) {
    await validateChain(ethSigner, config.immutableXConfig);
    const user = await ethSigner.getAddress();
    const { immutableXConfig } = config;
    const depositsApi = new index$2.DepositsApi(immutableXConfig.apiConfiguration);
    const encodingApi = new index$2.EncodingApi(immutableXConfig.apiConfiguration);
    const data = {
        token_address: deposit.tokenAddress,
        token_id: deposit.tokenId,
    };
    const amount = '1';
    const getSignableDepositRequest = {
        user,
        token: {
            type: deposit.type,
            data,
        },
        amount: amount.toString(),
    };
    const signableDepositResult = await depositsApi.getSignableDeposit({
        getSignableDepositRequest,
    });
    // Perform encoding on asset details to get an assetType (required for stark contract request)
    const encodingResult = await encodingApi.encodeAsset({
        assetType: 'asset',
        encodeAssetRequest: {
            token: {
                type: deposit.type,
                data: {
                    token_address: deposit.tokenAddress,
                    token_id: deposit.tokenId,
                },
            },
        },
    });
    const assetType = encodingResult.data.asset_type;
    const starkPublicKey = signableDepositResult.data.stark_key;
    const vaultId = signableDepositResult.data.vault_id;
    // Approve whether an amount of token from an account can be spent by a third-party account
    const tokenContract = exportContracts.IERC721.connect(deposit.tokenAddress, ethSigner);
    const operator = immutableXConfig.ethConfiguration.coreContractAddress;
    const isApprovedForAll = await tokenContract.isApprovedForAll(user, operator);
    if (!isApprovedForAll) {
        await tokenContract.setApprovalForAll(operator, true);
    }
    return executeDepositERC721(ethSigner, deposit.tokenId, assetType, starkPublicKey, vaultId, immutableXConfig);
}

// TODO: remove once fixed deposit variable shadowing
// eslint-disable-next-line consistent-return, @typescript-eslint/no-shadow
async function deposit({ signers, deposit, config }) {
    // TODO: please add a reasonable default here
    // eslint-disable-next-line default-case
    switch (deposit.type) {
        case 'ETH':
            return depositEth({ signers, deposit, config });
        case 'ERC20':
            return depositERC20({ signers, deposit, config });
        case 'ERC721':
            return depositERC721({ signers, deposit, config });
    }
}

async function exchangeTransfer({ signers, request, config, }) {
    await validateChain(signers.ethSigner, config.immutableXConfig);
    const exchangeApi = new index$2.ExchangesApi(config.immutableXConfig.apiConfiguration);
    const ethAddress = await signers.ethSigner.getAddress();
    const transferAmount = request.amount;
    const signableResult = await exchangeApi.getExchangeSignableTransfer({
        id: request.transactionID,
        getSignableTransferRequest: {
            sender: ethAddress,
            token: convertToSignableToken(request),
            amount: transferAmount,
            receiver: request.receiver,
        },
    });
    const { signable_message: signableMessage, payload_hash: payloadHash } = signableResult.data;
    const ethSignature = await signRaw(signableMessage, signers.ethSigner);
    const starkSignature = await signers.starkSigner.signMessage(payloadHash);
    const transferSigningParams = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sender_stark_key: signableResult.data.sender_stark_key,
        sender_vault_id: signableResult.data.sender_vault_id,
        receiver_stark_key: signableResult.data.receiver_stark_key,
        receiver_vault_id: signableResult.data.receiver_vault_id,
        asset_id: signableResult.data.asset_id,
        amount: signableResult.data.amount,
        nonce: signableResult.data.nonce,
        expiration_timestamp: signableResult.data.expiration_timestamp,
        stark_signature: starkSignature,
    };
    const response = await exchangeApi.createExchangeTransfer({
        id: request.transactionID,
        createTransferRequest: transferSigningParams,
        xImxEthAddress: ethAddress,
        xImxEthSignature: ethSignature,
    });
    return {
        sent_signature: response?.data.sent_signature,
        status: response?.data.status?.toString(),
        time: response?.data.time,
        transfer_id: response?.data.transfer_id,
    };
}

class GenericIMXProvider {
    config;
    signers;
    constructor(config, ethSigner, starkSigner) {
        this.config = config;
        this.signers = { ethSigner, starkSigner };
    }
    async getAddress() {
        return this.signers.ethSigner.getAddress();
    }
    async isRegisteredOffchain() {
        const ethAddress = await this.getAddress();
        return isRegisteredOffchain(ethAddress, this.config);
    }
    registerOffchain() {
        return registerOffchain(this.signers, this.config);
    }
    batchNftTransfer(request) {
        return batchTransfer({
            signers: this.signers,
            request,
            config: this.config,
        });
    }
    cancelOrder(request) {
        return cancelOrder({
            signers: this.signers,
            request,
            config: this.config,
        });
    }
    completeWithdrawal(starkPublicKey, token) {
        return completeWithdrawal({
            config: this.config,
            signers: this.signers,
            token,
            starkPublicKey,
        });
    }
    createOrder(request) {
        return createOrder({
            signers: this.signers,
            request,
            config: this.config,
        });
    }
    createTrade(request) {
        return createTrade({
            signers: this.signers,
            request,
            config: this.config,
        });
    }
    deposit(tokenAmount) {
        return deposit({
            signers: this.signers,
            deposit: tokenAmount,
            config: this.config,
        });
    }
    exchangeTransfer(request) {
        return exchangeTransfer({
            signers: this.signers,
            request,
            config: this.config,
        });
    }
    async isRegisteredOnchain() {
        const starkPublicKey = await this.signers.starkSigner.getAddress();
        return isRegisteredOnChain(starkPublicKey, this.signers.ethSigner, this.config);
    }
    prepareWithdrawal(request) {
        return prepareWithdrawal({
            signers: this.signers,
            withdrawal: request,
            config: this.config,
        });
    }
    transfer(request) {
        return transfer({
            signers: this.signers,
            request,
            config: this.config,
        });
    }
}

const WALLET_ACTION = {
    // TODO: remove once fixed - consider using an enum
    // eslint-disable-next-line @typescript-eslint/naming-convention
    SWITCH_CHAIN: 'wallet_switchEthereumChain',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CONNECT: 'eth_requestAccounts',
};
function isRequestableProvider(provider) {
    return !!provider?.request;
}
async function connectProvider(provider, chainID) {
    await provider.request({ method: WALLET_ACTION.CONNECT });
    if (chainID) {
        await provider.request({
            method: WALLET_ACTION.SWITCH_CHAIN,
            params: [{ chainId: `0x${chainID.toString(16)}` }],
        });
    }
}

const ERRORS = {
    // TODO: remove once fixed - consider using something in line with the naming convention
    // eslint-disable-next-line @typescript-eslint/naming-convention
    PROVIDER_NOT_FOUND: 'The Metamask provider was not found',
};
async function connect$1({ chainID, }) {
    const provider = (await detectEthereumProvider());
    if (!isRequestableProvider(provider)) {
        throw new Error(ERRORS.PROVIDER_NOT_FOUND);
    }
    await connectProvider(provider, chainID);
    // NOTE: if we want to listen to Metamask events in the future, we can add a
    // listener here.
    return new ethers.providers.Web3Provider(provider);
}

const COMMUNICATION_TYPE = 'message';
var RequestEventType;
(function (RequestEventType) {
    RequestEventType["GET_CONNECTION_REQUEST"] = "GET_CONNECTION_REQUEST";
    RequestEventType["CONNECT_WALLET_REQUEST"] = "CONNECT_WALLET_REQUEST";
    RequestEventType["SIGN_MESSAGE_REQUEST"] = "SIGN_MESSAGE_REQUEST";
    RequestEventType["DISCONNECT_WALLET_REQUEST"] = "DISCONNECT_WALLET_REQUEST";
    RequestEventType["GET_Y_COORDINATE_REQUEST"] = "GET_Y_COORDINATE_REQUEST";
})(RequestEventType || (RequestEventType = {}));
var ResponseEventType;
(function (ResponseEventType) {
    ResponseEventType["CONNECT_WALLET_RESPONSE"] = "CONNECT_WALLET_RESPONSE";
    ResponseEventType["SIGN_MESSAGE_RESPONSE"] = "SIGN_MESSAGE_RESPONSE";
    ResponseEventType["GET_CONNECTION_RESPONSE"] = "GET_CONNECTION_RESPONSE";
    ResponseEventType["DISCONNECT_WALLET_RESPONSE"] = "DISCONNECT_WALLET_RESPONSE";
    ResponseEventType["GET_Y_COORDINATE_RESPONSE"] = "GET_Y_COORDINATE_RESPONSE";
})(ResponseEventType || (ResponseEventType = {}));

function postRequestMessage(iframe, payload) {
    if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(payload, new URL(iframe.src).origin);
    }
}

function messageResponseListener(iframe, event, eventType, callback) {
    if (iframe && event.source !== iframe.contentWindow) {
        return;
    }
    const l2WalletMessage = event.data;
    if (l2WalletMessage.type !== eventType) {
        return;
    }
    callback(l2WalletMessage.details);
}

class ImxSigner {
    publicAddress;
    iframe;
    constructor(publicAddress, iframe) {
        this.publicAddress = publicAddress;
        this.iframe = iframe;
    }
    getAddress() {
        return this.publicAddress;
    }
    signMessage(rawMessage) {
        return new Promise((resolve, reject) => {
            const listener = (event) => {
                messageResponseListener(this.iframe, event, ResponseEventType.SIGN_MESSAGE_RESPONSE, (messageDetails) => {
                    window.removeEventListener(COMMUNICATION_TYPE, listener);
                    if (!messageDetails.success) {
                        reject(new Error(messageDetails.error?.message));
                    }
                    resolve(messageDetails.data.signedMessage);
                });
            };
            window.addEventListener(COMMUNICATION_TYPE, listener);
            postRequestMessage(this.iframe, {
                type: RequestEventType.SIGN_MESSAGE_REQUEST,
                details: { starkPublicKey: this.publicAddress, message: rawMessage },
            });
        });
    }
    getIFrame() {
        return this.iframe;
    }
    getYCoordinate() {
        return new Promise((resolve, reject) => {
            const listener = (event) => {
                messageResponseListener(this.iframe, event, ResponseEventType.GET_Y_COORDINATE_RESPONSE, (messageDetails) => {
                    window.removeEventListener(COMMUNICATION_TYPE, listener);
                    if (!messageDetails.success) {
                        reject(new Error(messageDetails.error?.message));
                    }
                    resolve(messageDetails.data.yCoordinate);
                });
            };
            window.addEventListener(COMMUNICATION_TYPE, listener);
            postRequestMessage(this.iframe, {
                type: RequestEventType.GET_Y_COORDINATE_REQUEST,
                details: { starkPublicKey: this.publicAddress },
            });
        });
    }
}

const IMX_WALLET_IFRAME_ID = 'imx-wallet-app';
const IMX_WALLET_IFRAME_HOSTS = {
    [Environment.SANDBOX]: 'https://wallets.sandbox.immutable.com',
    [Environment.PRODUCTION]: 'https://wallets.immutable.com',
};
const IMX_WALLET_IFRAME_STYLE = 'display: none;';
function getIFrame() {
    return document.querySelector(`iframe#${IMX_WALLET_IFRAME_ID}`);
}
async function setupIFrame(env) {
    return new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('id', IMX_WALLET_IFRAME_ID);
        iframe.setAttribute('src', IMX_WALLET_IFRAME_HOSTS[env]);
        iframe.setAttribute('style', IMX_WALLET_IFRAME_STYLE);
        document.body.appendChild(iframe);
        iframe.onload = () => resolve(iframe);
    });
}
async function getOrSetupIFrame(env) {
    const iframe = getIFrame();
    if (iframe)
        return iframe;
    return await setupIFrame(env);
}

const DEFAULT_CONNECTION_MESSAGE = 'Only sign this request if you’ve initiated an action with Immutable X.';
const CONNECTION_FAILED_ERROR = 'The L2 IMX Wallet connection has failed';
async function connect(l1Provider, env) {
    const l1Signer = l1Provider.getSigner();
    const address = await l1Signer.getAddress();
    const signature = await l1Signer.signMessage(DEFAULT_CONNECTION_MESSAGE);
    const iframe = await getOrSetupIFrame(env);
    return new Promise((resolve, reject) => {
        const listener = (event) => {
            messageResponseListener(iframe, event, ResponseEventType.CONNECT_WALLET_RESPONSE, (messageDetails) => {
                window.removeEventListener(COMMUNICATION_TYPE, listener);
                if (!messageDetails.success) {
                    reject(new Error(CONNECTION_FAILED_ERROR));
                }
                resolve(new ImxSigner(messageDetails.data.starkPublicKey, iframe));
            });
        };
        window.addEventListener(COMMUNICATION_TYPE, listener);
        postRequestMessage(iframe, {
            type: RequestEventType.CONNECT_WALLET_REQUEST,
            details: { ethAddress: address, signature },
        });
    });
}
async function disconnect(imxSigner) {
    const iframe = imxSigner.getIFrame();
    return new Promise((resolve, reject) => {
        const listener = (event) => {
            messageResponseListener(iframe, event, ResponseEventType.DISCONNECT_WALLET_RESPONSE, (messageDetails) => {
                window.removeEventListener(COMMUNICATION_TYPE, listener);
                if (!messageDetails.success && messageDetails.error) {
                    reject(messageDetails.error);
                }
                iframe.remove();
                resolve();
            });
        };
        window.addEventListener(COMMUNICATION_TYPE, listener);
        postRequestMessage(iframe, {
            type: RequestEventType.DISCONNECT_WALLET_REQUEST,
            details: { starkPublicKey: imxSigner.getAddress() },
        });
    });
}

var ProviderErrorType;
(function (ProviderErrorType) {
    ProviderErrorType["PROVIDER_CONNECTION_ERROR"] = "PROVIDER_CONNECTION_ERROR";
    ProviderErrorType["WALLET_CONNECTION_ERROR"] = "WALLET_CONNECTION_ERROR";
})(ProviderErrorType || (ProviderErrorType = {}));
class ProviderError extends Error {
    type;
    constructor(message, type) {
        super(message);
        this.type = type;
    }
}
const withProviderError = async (fn, customError) => {
    try {
        return await fn();
    }
    catch (error) {
        const errorMessage = customError.message || `${error.message}` || 'UnknownError';
        throw new ProviderError(errorMessage, customError.type);
    }
};

class MetaMaskIMXProvider extends GenericIMXProvider {
    static imxSigner;
    static async connect(config) {
        return await withProviderError(async () => {
            const metaMaskProvider = await connect$1({
                chainID: config.immutableXConfig.ethConfiguration.chainID,
            });
            this.imxSigner = await connect(metaMaskProvider, config.baseConfig.environment);
            return new MetaMaskIMXProvider(config, metaMaskProvider.getSigner(), this.imxSigner);
        }, { type: ProviderErrorType.WALLET_CONNECTION_ERROR });
    }
    static async disconnect() {
        if (!this.imxSigner) {
            throw new ProviderError('Attempted to disconnect from the MetaMask IMX provider without an established connection', ProviderErrorType.PROVIDER_CONNECTION_ERROR);
        }
        return withProviderError(async () => {
            await disconnect(this.imxSigner);
        }, { type: ProviderErrorType.PROVIDER_CONNECTION_ERROR });
    }
    static async signMessage(message) {
        if (!this.imxSigner) {
            throw new ProviderError('Attempted to sign a message with the MetaMask IMX provider without an established connection', ProviderErrorType.PROVIDER_CONNECTION_ERROR);
        }
        return withProviderError(async () => await this.imxSigner.signMessage(message), { type: ProviderErrorType.PROVIDER_CONNECTION_ERROR });
    }
}

class ProviderConfiguration {
    immutableXConfig;
    baseConfig;
    constructor({ baseConfig, overrides }) {
        this.baseConfig = baseConfig;
        if (overrides) {
            this.immutableXConfig = overrides.immutableXConfig;
        }
        else {
            const clientConfig = new ImxConfiguration({ baseConfig });
            this.immutableXConfig = clientConfig.immutableXConfig;
        }
    }
}

var x = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ApiConfiguration: ApiConfiguration,
    AssetsApi: AssetsApi,
    BalancesApi: BalancesApi,
    CollectionsApi: CollectionsApi,
    Contracts: exportContracts,
    DepositsApi: DepositsApi,
    EncodingApi: EncodingApi,
    get Environment () { return Environment; },
    EthSigner: Signer,
    ExchangesApi: ExchangesApi,
    GenericIMXProvider: GenericIMXProvider,
    IMXClient: IMXClient,
    IMXError: IMXError,
    ImmutableConfiguration: ImmutableConfiguration,
    ImmutableX: ImmutableX,
    ImxConfiguration: ImxConfiguration,
    MetaMaskIMXProvider: MetaMaskIMXProvider,
    MetadataApi: MetadataApi,
    MetadataRefreshesApi: MetadataRefreshesApi,
    MetadataSchemaRequestTypeEnum: MetadataSchemaRequestTypeEnum,
    MintsApi: MintsApi,
    NftCheckoutPrimaryApi: NftCheckoutPrimaryApi,
    OrdersApi: OrdersApi,
    ProjectsApi: ProjectsApi,
    ProviderConfiguration: ProviderConfiguration,
    TokensApi: TokensApi,
    TradesApi: TradesApi,
    TransfersApi: TransfersApi,
    UsersApi: UsersApi,
    WithdrawalsApi: WithdrawalsApi,
    createConfig: createConfig,
    createImmutableXConfiguration: createImmutableXConfiguration,
    createStarkSigner: createStarkSigner,
    generateLegacyStarkPrivateKey: generateLegacyStarkPrivateKey,
    generateStarkPrivateKey: generateStarkPrivateKey,
    imxClientConfig: imxClientConfig,
    imxClientCreateStarkSigner: createStarkSigner,
    imxClientGenerateLegacyStarkPrivateKey: generateLegacyStarkPrivateKey,
    production: production,
    sandbox: sandbox,
    serializePackedSignature: serializePackedSignature,
    signRegisterEthAddress: signRegisterEthAddress,
    starkEcOrder: starkEcOrder
});

export { GenericIMXProvider as G, MetaMaskIMXProvider as M, ProviderConfiguration as P, x };
